/**
 * a.Newton Chat Widget
 * 
 * Slide-out chat panel for chatting with a.Newton about physics
 */

import { CONFIG } from './config.js'

class NewtonChat {
  constructor() {
    this.isOpen = false
    this.messages = []
    this.supabaseUrl = CONFIG.supabaseUrl
    this.supabaseAnonKey = CONFIG.supabaseAnonKey
    this.facultyId = CONFIG.facultyId
    this.physicsWebUrl = CONFIG.physicsWebUrl
    
    this.init()
  }

  init() {
    this.injectStyles()
    this.createChatPanel()
    this.setupEventListeners()
  }

  injectStyles() {
    if (document.getElementById('newton-chat-styles')) return

    const style = document.createElement('style')
    style.id = 'newton-chat-styles'
    style.textContent = `
      .newton-chat-toggle {
        position: fixed;
        right: 20px;
        bottom: 20px;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: #4a9eff;
        color: white;
        border: none;
        font-size: 24px;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s, background 0.2s;
      }

      .newton-chat-toggle:hover {
        background: #3a8eef;
        transform: scale(1.05);
      }

      .newton-chat-panel {
        position: fixed;
        right: -450px;
        top: 0;
        width: 450px;
        height: 100vh;
        background: #2a2a2a;
        border-left: 1px solid #3a3a3a;
        z-index: 999;
        display: flex;
        flex-direction: column;
        transition: right 0.3s ease;
        box-shadow: -4px 0 12px rgba(0, 0, 0, 0.3);
      }

      .newton-chat-panel.open {
        right: 0;
      }

      .newton-chat-header {
        background: #1a1a1a;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #3a3a3a;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .newton-chat-header h3 {
        color: #fff;
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .newton-chat-close {
        background: none;
        border: none;
        color: #999;
        font-size: 24px;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s, color 0.2s;
      }

      .newton-chat-close:hover {
        background: #3a3a3a;
        color: #fff;
      }

      .newton-chat-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .newton-chat-tabs {
        display: flex;
        background: #1a1a1a;
        border-bottom: 1px solid #3a3a3a;
      }

      .newton-chat-tab {
        flex: 1;
        padding: 0.75rem 1rem;
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        font-size: 0.9rem;
        transition: all 0.2s;
        border-bottom: 2px solid transparent;
      }

      .newton-chat-tab.active {
        color: #4a9eff;
        border-bottom-color: #4a9eff;
        background: #2a2a2a;
      }

      .newton-chat-tab:hover:not(.active) {
        background: #222;
        color: #ccc;
      }

      .newton-chat-tab-content {
        display: none;
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
      }

      .newton-chat-tab-content.active {
        display: flex;
        flex-direction: column;
      }

      .newton-chat-messages {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .newton-chat-controls-section {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .newton-control-group {
        margin-bottom: 1rem;
      }

      .newton-control-group h4 {
        font-size: 0.9rem;
        margin-bottom: 0.75rem;
        color: #fff;
        font-weight: 600;
      }

      .newton-scenario-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .newton-scenario-btn {
        width: 100%;
        text-align: left;
        padding: 0.75rem;
        background: #3a3a3a;
        color: #e0e0e0;
        border: none;
        border-radius: 6px;
        font-size: 0.9rem;
        cursor: pointer;
        transition: background 0.2s;
      }

      .newton-scenario-btn:hover {
        background: #4a4a4a;
      }

      .newton-control-btn {
        width: 100%;
        padding: 0.75rem;
        background: #4a9eff;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
        margin-bottom: 0.5rem;
      }

      .newton-control-btn:hover {
        background: #3a8eef;
      }

      .newton-control-btn.secondary {
        background: #4a4a4a;
      }

      .newton-control-btn.secondary:hover {
        background: #5a5a5a;
      }

      .newton-status {
        padding: 0.75rem;
        background: #1a1a1a;
        border-radius: 6px;
        font-size: 0.85rem;
        color: #999;
        margin-top: 1rem;
      }

      .newton-status.loading {
        color: #4a9eff;
      }

      .newton-status.error {
        color: #ff4a4a;
      }

      .newton-status.success {
        color: #4aff4a;
      }

      .newton-chat-message {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        animation: slideIn 0.3s ease;
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .newton-chat-message.user {
        align-items: flex-end;
      }

      .newton-chat-message.assistant {
        align-items: flex-start;
      }

      .newton-chat-bubble {
        max-width: 80%;
        padding: 0.75rem 1rem;
        border-radius: 12px;
        word-wrap: break-word;
        line-height: 1.5;
      }

      .newton-chat-message.user .newton-chat-bubble {
        background: #4a9eff;
        color: white;
      }

      .newton-chat-message.assistant .newton-chat-bubble {
        background: #3a3a3a;
        color: #e0e0e0;
      }

      .newton-chat-sender {
        font-size: 0.75rem;
        color: #999;
        padding: 0 0.5rem;
      }

      .newton-chat-input-container {
        padding: 1rem;
        background: #1a1a1a;
        border-top: 1px solid #3a3a3a;
        display: flex;
        gap: 0.5rem;
      }

      .newton-chat-input {
        flex: 1;
        background: #3a3a3a;
        border: 1px solid #4a4a4a;
        border-radius: 8px;
        padding: 0.75rem 1rem;
        color: #e0e0e0;
        font-size: 0.9rem;
        outline: none;
        transition: border-color 0.2s;
      }

      .newton-chat-input:focus {
        border-color: #4a9eff;
      }

      .newton-chat-input::placeholder {
        color: #666;
      }

      .newton-chat-send {
        background: #4a9eff;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 0.75rem 1.5rem;
        font-size: 0.9rem;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }

      .newton-chat-send:hover:not(:disabled) {
        background: #3a8eef;
      }

      .newton-chat-send:disabled {
        background: #4a4a4a;
        cursor: not-allowed;
        opacity: 0.5;
      }

      .newton-chat-status {
        padding: 0.5rem 1rem;
        font-size: 0.75rem;
        color: #999;
        text-align: center;
        background: #1a1a1a;
        border-top: 1px solid #3a3a3a;
      }

      .newton-chat-status.connected {
        color: #4aff4a;
      }

      .newton-chat-status.error {
        color: #ff4a4a;
      }

      .newton-chat-status.loading {
        color: #4a9eff;
      }

      .newton-chat-welcome {
        padding: 1rem;
        text-align: center;
        color: #999;
        font-size: 0.85rem;
        line-height: 1.6;
      }

      @media (max-width: 768px) {
        .newton-chat-panel {
          width: 100%;
          right: -100%;
        }
      }
    `
    document.head.appendChild(style)
  }

  createChatPanel() {
    // Check if already exists
    if (document.getElementById('newton-chat-panel')) {
      return
    }

    const panel = document.createElement('div')
    panel.className = 'newton-chat-panel'
    panel.id = 'newton-chat-panel'
    panel.innerHTML = `
      <div class="newton-chat-header">
        <h3>
          <span>🔬</span>
          <span>a.Newton</span>
        </h3>
        <button class="newton-chat-close" aria-label="Close chat">×</button>
      </div>
      <div class="newton-chat-tabs">
        <button class="newton-chat-tab active" data-tab="chat">💬 Chat</button>
        <button class="newton-chat-tab" data-tab="controls">⚙️ Controls</button>
      </div>
      <div class="newton-chat-content">
        <!-- Chat Tab -->
        <div class="newton-chat-tab-content active" id="newton-chat-tab-content">
          <div class="newton-chat-messages" id="newton-chat-messages">
            <div class="newton-chat-welcome">
              <p>👋 Hello! I'm a.Newton, here to help you understand classical mechanics.</p>
              <p>Ask me about physics, request simulations, or get explanations!</p>
            </div>
          </div>
          <div class="newton-chat-input-container">
            <input 
              type="text" 
              class="newton-chat-input" 
              id="newton-chat-input"
              placeholder="Ask a.Newton about physics..."
              autocomplete="off"
            />
            <button class="newton-chat-send" id="newton-chat-send">Send</button>
          </div>
          <div class="newton-chat-status" id="newton-chat-status">Ready</div>
        </div>
        <!-- Controls Tab -->
        <div class="newton-chat-tab-content" id="newton-controls-tab-content">
          <div class="newton-chat-controls-section">
            <div class="newton-control-group">
              <h4>Scenarios</h4>
              <div class="newton-scenario-list">
                <button class="newton-scenario-btn" data-scenario="newtons_cradle">
                  ⚖️ Newton's Cradle
                </button>
                <button class="newton-scenario-btn" data-scenario="pendulum">
                  🎯 Simple Pendulum
                </button>
                <button class="newton-scenario-btn" data-scenario="double_pendulum">
                  🔄 Double Pendulum
                </button>
                <button class="newton-scenario-btn" data-scenario="projectile">
                  ⚽ Projectile Motion
                </button>
                <button class="newton-scenario-btn" data-scenario="spring">
                  🌀 Spring-Mass System
                </button>
              </div>
            </div>
            <div class="newton-control-group">
              <h4>Simulation Controls</h4>
              <button class="newton-control-btn" id="newton-play-pause">⏸️ Pause</button>
              <button class="newton-control-btn secondary" id="newton-reset">↺ Reset</button>
            </div>
            <div class="newton-status" id="newton-status">Ready</div>
          </div>
        </div>
      </div>
    `
    document.body.appendChild(panel)

    // Check if toggle already exists
    if (!document.getElementById('newton-chat-toggle')) {
      const toggle = document.createElement('button')
      toggle.className = 'newton-chat-toggle'
      toggle.id = 'newton-chat-toggle'
      toggle.innerHTML = '💬'
      toggle.setAttribute('aria-label', 'Open chat with a.Newton')
      document.body.appendChild(toggle)
    }
  }

  setupEventListeners() {
    const toggle = document.getElementById('newton-chat-toggle')
    const close = document.querySelector('.newton-chat-close')
    const sendButton = document.getElementById('newton-chat-send')
    const input = document.getElementById('newton-chat-input')
    const panel = document.getElementById('newton-chat-panel')

    toggle?.addEventListener('click', () => this.toggle())
    close?.addEventListener('click', () => this.close())
    
    sendButton?.addEventListener('click', () => this.sendMessage())
    
    input?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    })

    // Tab switching
    const tabs = panel?.querySelectorAll('.newton-chat-tab')
    tabs?.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.tab
        this.switchTab(tabName)
      })
    })

    // Scenario buttons
    const scenarioButtons = panel?.querySelectorAll('.newton-scenario-btn')
    scenarioButtons?.forEach(btn => {
      btn.addEventListener('click', () => {
        const scenario = btn.dataset.scenario
        this.loadScenario(scenario)
      })
    })

    // Control buttons
    const playPauseBtn = document.getElementById('newton-play-pause')
    const resetBtn = document.getElementById('newton-reset')
    
    playPauseBtn?.addEventListener('click', () => {
      this.togglePlayPause()
    })
    
    resetBtn?.addEventListener('click', () => {
      this.resetSimulation()
    })
  }

  switchTab(tabName) {
    const panel = document.getElementById('newton-chat-panel')
    if (!panel) return

    // Update tab buttons
    const tabs = panel.querySelectorAll('.newton-chat-tab')
    tabs.forEach(tab => {
      if (tab.dataset.tab === tabName) {
        tab.classList.add('active')
      } else {
        tab.classList.remove('active')
      }
    })

    // Update tab content
    const contents = panel.querySelectorAll('.newton-chat-tab-content')
    contents.forEach(content => {
      if (content.id === `newton-${tabName}-tab-content` || 
          (tabName === 'chat' && content.id === 'newton-chat-tab-content')) {
        content.classList.add('active')
      } else {
        content.classList.remove('active')
      }
    })
  }

  loadScenario(scenario) {
    // Dispatch custom event that mujoco-viewer.js can listen to
    const event = new CustomEvent('loadScenario', { detail: { scenario } })
    window.dispatchEvent(event)
    
    // Switch to chat tab to see status
    this.switchTab('chat')
  }

  togglePlayPause() {
    const event = new CustomEvent('togglePlayPause')
    window.dispatchEvent(event)
  }

  resetSimulation() {
    const event = new CustomEvent('resetSimulation')
    window.dispatchEvent(event)
  }

  updateStatus(message, type = '') {
    const status = document.getElementById('newton-status')
    if (status) {
      status.textContent = message
      status.className = `newton-status ${type}`
    }
    
    // Also update chat status
    const chatStatus = document.getElementById('newton-chat-status')
    if (chatStatus) {
      chatStatus.textContent = message
      chatStatus.className = `newton-chat-status ${type}`
    }
  }

  toggle() {
    this.isOpen = !this.isOpen
    const panel = document.getElementById('newton-chat-panel')
    if (panel) {
      panel.classList.toggle('open', this.isOpen)
    }
    
    if (this.isOpen) {
      const input = document.getElementById('newton-chat-input')
      input?.focus()
    }
  }

  close() {
    this.isOpen = false
    const panel = document.getElementById('newton-chat-panel')
    if (panel) {
      panel.classList.remove('open')
    }
  }

  async sendMessage() {
    const input = document.getElementById('newton-chat-input')
    const message = input?.value.trim()
    
    if (!message) return

    // Add user message to UI
    this.addMessage('You', message, 'user')
    input.value = ''

    // Disable input while sending
    const sendButton = document.getElementById('newton-chat-send')
    if (sendButton) sendButton.disabled = true
    this.updateStatus('a.Newton is thinking...', 'loading')

    try {
      // Call ask-faculty Edge Function
      // Note: ask-faculty may require authentication
      // For public access, we might need to use a different endpoint or handle auth
      const headers = {
        'Content-Type': 'application/json',
      }
      
      if (this.supabaseAnonKey) {
        headers['Authorization'] = `Bearer ${this.supabaseAnonKey}`
        headers['apikey'] = this.supabaseAnonKey
      }
      
      const response = await fetch(`${this.supabaseUrl}/functions/v1/ask-faculty`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          faculty_id: this.facultyId,
          message: message,
          conversation_history: this.messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
          context: 'dialogue',
          use_rag: true,
          use_commonplace: true,
          temperature: 0.8,
          max_tokens: 1000,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const data = await response.json()
      const reply = data.response || 'I apologize, but I could not generate a response.'

      // Add assistant response
      this.addMessage('a.Newton', reply, 'assistant')

      // Update conversation history
      this.messages.push({ role: 'user', content: message })
      this.messages.push({ role: 'assistant', content: reply })

      this.updateStatus('Ready', 'connected')

      // Check if response contains simulation request
      this.checkForSimulationRequest(reply, message)
    } catch (error) {
      console.error('Error sending message:', error)
      this.addMessage('System', `Error: ${error.message}. Please try again.`, 'assistant')
      this.updateStatus('Error - try again', 'error')
    } finally {
      if (sendButton) sendButton.disabled = false
      input?.focus()
    }
  }

  checkForSimulationRequest(reply, userMessage) {
    // Check if a.Newton mentioned creating a simulation or if user requested one
    const lowerReply = reply.toLowerCase()
    const lowerMessage = userMessage.toLowerCase()
    
    const isSimulationRequest = 
      lowerMessage.includes('show me') ||
      lowerMessage.includes('create') ||
      lowerMessage.includes('simulate') ||
      lowerMessage.includes('demonstrate') ||
      lowerReply.includes('simulation') ||
      lowerReply.includes('simulation url')
    
    if (isSimulationRequest) {
      // Check if reply contains a simulation URL or DSL
      // If a.Newton generated DSL, we could parse it and create a simulation
      // For now, show a helpful message
      const hasUrl = reply.includes('http') || reply.includes('sim?')
      
      if (!hasUrl) {
        this.addMessage('System', 
          '💡 Tip: I can create custom physics simulations! Try asking: "Show me a pendulum with a 2kg mass"', 
          'assistant'
        )
      }
    }
  }

  addMessage(sender, content, role) {
    const messagesContainer = document.getElementById('newton-chat-messages')
    if (!messagesContainer) return

    // Remove welcome message if present
    const welcome = messagesContainer.querySelector('.newton-chat-welcome')
    if (welcome) welcome.remove()

    const messageDiv = document.createElement('div')
    messageDiv.className = `newton-chat-message ${role}`
    messageDiv.innerHTML = `
      <div class="newton-chat-sender">${sender}</div>
      <div class="newton-chat-bubble">${this.formatMessage(content)}</div>
    `
    messagesContainer.appendChild(messageDiv)
    messagesContainer.scrollTop = messagesContainer.scrollHeight
  }

  formatMessage(content) {
    // Basic markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br>')
  }

  updateStatus(message, type = '') {
    const status = document.getElementById('newton-chat-status')
    if (status) {
      status.textContent = message
      status.className = `newton-chat-status ${type}`
    }
  }
}

// Initialize chat when DOM is ready
function initNewtonChat() {
  try {
    window.newtonChat = new NewtonChat()
  } catch (error) {
    console.error('Failed to initialize Newton chat:', error)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewtonChat)
} else {
  // DOM already ready, but wait a tick to ensure all elements exist
  setTimeout(initNewtonChat, 0)
}
